import React from "react";
import { useCan } from "./useCan";
import { Lock } from "lucide-react";

/**
 * <Can userPermissions={permissionsFromApi} resource="organization" action="create">
 *   <Button />
 * </Can>
 *
 * behavior:
 *  - "hide": render nothing (default)
 *  - "disable": keep element but disable it (best for UX sometimes)
 */

export function Can({
  userPermissions,
  resource,
  action,
  required, // string or array
  mode = "all",
  behavior = "hide", // "hide" | "disable"
  fallback = null,
  enabled = true,
  children,
  showMessage = false,
  defaultView = false,
}) {
  if (defaultView) return <>{children}</>;

  const { allowed } = useCan({
    userPermissions,
    resource,
    action,
    required,
    mode,
    enabled,
  });

  const Message = showMessage ? (
    <div className="w-full flex justify-center py-12">
      <div className="border border-dashed rounded-lg p-8 max-w-md text-center">
        <div className="text-4xl mb-3 flex justify-center">
          <Lock className="w-8 h-8 text-emerald-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-500">
          View Permission Required
        </h3>
        <p className="mt-2 text-gray-500">
          You are not allowed to access this section.
        </p>
      </div>
    </div>
  ) : null;

  if (allowed) return <>{children}</>;

  if (behavior === "hide") {
    if (fallback) return <>{fallback}</>;
    return Message;
  }

  // behavior === "disable"
  const child = React.Children.only(children);
  if (!React.isValidElement(child)) return fallback ? <>{fallback}</> : null;

  return (
    <>
      {React.cloneElement(child, {
        ...child.props,
        disabled: true,
        "aria-disabled": true,
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
      })}
      {Message}
    </>
  );
}
